import { createRoot } from 'react-dom/client';
import React from 'react';
import html2canvas from 'html2canvas';
import GiftCardPreview from '../components/GiftCardPreview';
import { GiftCardSettings } from './giftCardSettingsService';

interface GenerateGiftCardImageOptions {
  giftCardCode: string;
  design: GiftCardSettings;
  businessLogoUrl?: string | null;
  recipientName?: string;
  width?: number; // Default 600px for email
  height?: number; // Calculated based on aspect ratio
}

/**
 * Generate a base64 image of the gift card
 */
export async function generateGiftCardImage(
  options: GenerateGiftCardImageOptions
): Promise<string> {
  const {
    giftCardCode,
    design,
    businessLogoUrl,
    recipientName,
    width = 600,
    height
  } = options;

  // Calculate height based on aspect ratio (1.586:1)
  const cardHeight = height || Math.round(width / 1.586);

  // Create a temporary container with proper styling
  // html2canvas needs the element to be in the viewport, so we'll position it
  // at the top-left corner but make it invisible to the user
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '0';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${cardHeight}px`;
  container.style.backgroundColor = 'transparent';
  container.style.overflow = 'visible';
  container.style.display = 'block';
  container.style.zIndex = '999999';
  container.style.visibility = 'visible';
  container.style.opacity = '0.01'; // Nearly invisible but still "visible" to html2canvas
  container.style.pointerEvents = 'none';
  container.style.clipPath = 'inset(0 0 0 0)'; // Keep it in viewport
  
  // Append to body
  document.body.appendChild(container);

  try {
    // Create React root and render the gift card
    const root = createRoot(container);
    
    root.render(
      React.createElement(GiftCardPreview, {
        settings: design,
        businessLogoUrl: businessLogoUrl,
        code: giftCardCode,
        recipientName: recipientName,
        showSampleData: false,
        baseWidth: width // Use the specified width for consistent rendering
      })
    );
    
    // Wait for React to render and styles to apply
    // Use multiple requestAnimationFrame calls to ensure DOM is fully ready
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 1000); // Longer initial wait
          });
        });
      });
    });

    // Find the actual gift card element - it's the div with fixed width/height
    // The GiftCardPreview renders a div with inline styles for width and height
    let elementToCapture: HTMLElement | null = null;
    
    // Wait a bit more and try multiple times to find the element
    for (let attempt = 0; attempt < 5; attempt++) {
      // Try to find the element by checking for the fixed width style
      const allDivs = container.querySelectorAll('div');
      for (const div of Array.from(allDivs)) {
        const divWidth = div.offsetWidth || div.getBoundingClientRect().width;
        // The gift card should have the exact width we specified (or close to it)
        if (divWidth > 0 && Math.abs(divWidth - width) < 50) { // Allow 50px tolerance
          // Check if it has content (children or text)
          if (div.children.length > 0 || div.textContent?.trim()) {
            elementToCapture = div as HTMLElement;
            console.log(`✅ Found gift card element on attempt ${attempt + 1}:`, {
              width: divWidth,
              height: div.offsetHeight,
              children: div.children.length,
              hasText: !!div.textContent?.trim()
            });
            break;
          }
        }
      }
      
      if (elementToCapture) break;
      
      // Wait a bit more before next attempt
      if (attempt < 4) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    // Fallback: use first child div that has dimensions
    if (!elementToCapture) {
      const firstChild = container.firstElementChild as HTMLElement;
      if (firstChild && (firstChild.offsetWidth > 0 || firstChild.children.length > 0)) {
        elementToCapture = firstChild;
        console.log('⚠️ Using first child as fallback');
      } else {
        elementToCapture = container;
        console.warn('⚠️ Using container itself as fallback');
      }
    }

    if (!elementToCapture) {
      throw new Error('Gift card element not found');
    }
    
    // Verify element has content
    if (elementToCapture.children.length === 0 && !elementToCapture.textContent?.trim()) {
      console.warn('⚠️ Element appears empty, waiting longer...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check again
      if (elementToCapture.children.length === 0 && !elementToCapture.textContent?.trim()) {
        console.error('❌ Element is still empty after waiting');
        throw new Error('Gift card element has no content');
      }
    }

    // Log element details for debugging
    console.log('📸 Gift card element found:', {
      tagName: elementToCapture.tagName,
      width: elementToCapture.offsetWidth,
      height: elementToCapture.offsetHeight,
      scrollWidth: elementToCapture.scrollWidth,
      scrollHeight: elementToCapture.scrollHeight,
      hasChildren: elementToCapture.children.length,
      backgroundColor: window.getComputedStyle(elementToCapture).backgroundColor
    });

    // Wait for images to load - both external and within the element
    const imagePromises: Promise<void>[] = [];
    
    // Pre-load business logo if provided
    if (businessLogoUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      imagePromises.push(
        new Promise<void>((resolve) => {
          img.onload = () => {
            console.log('✅ Business logo loaded');
            resolve();
          };
          img.onerror = () => {
            console.warn('⚠️ Business logo failed to load');
            resolve(); // Continue even if image fails
          };
          img.src = businessLogoUrl;
          // Timeout after 5 seconds
          setTimeout(() => {
            console.warn('⚠️ Business logo load timeout');
            resolve();
          }, 5000);
        })
      );
    }

    // Wait for all images inside the element to load
    const images = elementToCapture.querySelectorAll('img');
    console.log(`🖼️ Found ${images.length} images in element`);
    
    Array.from(images).forEach((img, index) => {
      if (img.complete && img.naturalHeight > 0) {
        console.log(`✅ Image ${index + 1} already loaded`);
        return;
      }
      
      imagePromises.push(
        new Promise<void>((resolve) => {
          const handleLoad = () => {
            console.log(`✅ Image ${index + 1} loaded`);
            resolve();
          };
          const handleError = () => {
            console.warn(`⚠️ Image ${index + 1} failed to load`);
            resolve();
          };
          
          img.addEventListener('load', handleLoad, { once: true });
          img.addEventListener('error', handleError, { once: true });
          
          // Force reload if src exists
          if (img.src) {
            const src = img.src;
            img.src = '';
            img.src = src;
          }
          
          // Timeout after 5 seconds
          setTimeout(() => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
            console.warn(`⚠️ Image ${index + 1} load timeout`);
            resolve();
          }, 5000);
        })
      );
    });
    
    // Wait for all images
    await Promise.all(imagePromises);
    console.log('✅ All images loaded');

    // Additional wait for fonts, gradients, and all styles to fully apply
    // Wait for fonts to load
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
      console.log('✅ Fonts loaded');
    }
    
    // Wait for all CSS and React rendering to be applied
    // Multiple passes to ensure everything is painted
    for (let i = 0; i < 3; i++) {
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 200);
          });
        });
      });
    }
    
    console.log('✅ All rendering passes complete');

    // Force multiple reflows to ensure all styles are computed
    void elementToCapture.offsetHeight;
    void elementToCapture.scrollHeight;
    void elementToCapture.offsetWidth;
    void elementToCapture.scrollWidth;
    
    // Force a repaint
    const computedStyle = window.getComputedStyle(elementToCapture);
    void computedStyle.backgroundColor;

    // Get actual dimensions from the rendered element
    // Use the element's actual rendered dimensions
    let actualWidth = elementToCapture.offsetWidth || elementToCapture.scrollWidth || width;
    let actualHeight = elementToCapture.offsetHeight || elementToCapture.scrollHeight || cardHeight;
    
    // Ensure we have valid dimensions - if zero, use fallback and set explicit styles
    if (actualWidth === 0 || actualHeight === 0) {
      console.warn('⚠️ Element has zero dimensions, setting explicit dimensions');
      // Set explicit dimensions on the element
      elementToCapture.style.width = width + 'px';
      elementToCapture.style.height = cardHeight + 'px';
      elementToCapture.style.display = 'block';
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Re-check dimensions
      actualWidth = elementToCapture.offsetWidth || width;
      actualHeight = elementToCapture.offsetHeight || cardHeight;
      
      console.log('📐 Using dimensions after fix:', { actualWidth, actualHeight });
    }

    console.log('📸 Capturing gift card:', {
      containerWidth: container.offsetWidth,
      containerHeight: container.offsetHeight,
      elementWidth: actualWidth,
      elementHeight: actualHeight,
      scrollWidth: elementToCapture.scrollWidth,
      scrollHeight: elementToCapture.scrollHeight,
      design: design.card_style,
      hasGradient: design.card_style === 'modern',
      background: design.background_color,
      elementHTML: elementToCapture.innerHTML.substring(0, 200) // First 200 chars for debugging
    });

    // Verify element has actual content before capturing
    const computedBgColor = window.getComputedStyle(elementToCapture).backgroundColor;
    const hasBackground = computedBgColor && computedBgColor !== 'rgba(0, 0, 0, 0)' && computedBgColor !== 'transparent';
    
    console.log('🔍 Pre-capture check:', {
      backgroundColor: computedBgColor,
      hasBackground,
      innerHTML: elementToCapture.innerHTML.substring(0, 100),
      childrenCount: elementToCapture.children.length,
      textContent: elementToCapture.textContent?.substring(0, 50)
    });
    
    if (!hasBackground && !elementToCapture.textContent?.trim() && elementToCapture.children.length === 0) {
      throw new Error('Element has no visible content to capture');
    }
    
    // Temporarily make element fully visible for capture
    const originalOpacity = elementToCapture.style.opacity;
    const originalVisibility = elementToCapture.style.visibility;
    const originalContainerOpacity = container.style.opacity;
    
    elementToCapture.style.opacity = '1';
    elementToCapture.style.visibility = 'visible';
    elementToCapture.style.position = 'relative'; // Ensure it's in normal flow
    
    // Also ensure container is visible
    container.style.opacity = '1';
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    
    // Wait multiple frames for all changes to take effect
    // Ensure element is actually visible and painted
    for (let i = 0; i < 5; i++) {
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 200);
          });
        });
      });
      
      // Force a repaint by reading layout properties
      void elementToCapture.offsetHeight;
      void elementToCapture.offsetWidth;
      void elementToCapture.getBoundingClientRect();
    }
    
    console.log('✅ Element made visible and painted');

    // Convert to canvas - capture the actual element with all its children
    const canvas = await html2canvas(elementToCapture, {
      scale: 2, // Higher resolution for email
      useCORS: true,
      allowTaint: false, // Set to false to allow CORS images
      backgroundColor: design.background_color || window.getComputedStyle(elementToCapture).backgroundColor || '#FFFFFF', // Use the card's background color, default to white
      width: actualWidth,
      height: actualHeight,
      logging: true, // Enable logging to debug
      removeContainer: false,
      imageTimeout: 15000, // Longer timeout for images
      foreignObjectRendering: true, // Better rendering for complex elements
      windowWidth: actualWidth,
      windowHeight: actualHeight,
      onclone: (clonedDoc, element) => {
        // Ensure all styles are preserved in the cloned document
        const clonedElement = element as HTMLElement;
        
        // Force computed styles from the original element
        const computedStyle = window.getComputedStyle(elementToCapture);
        clonedElement.style.width = `${actualWidth}px`;
        clonedElement.style.height = `${actualHeight}px`;
        clonedElement.style.backgroundColor = computedStyle.backgroundColor || design.background_color || '#FFFFFF';
        clonedElement.style.backgroundImage = computedStyle.backgroundImage;
        clonedElement.style.backgroundSize = computedStyle.backgroundSize;
        clonedElement.style.backgroundPosition = computedStyle.backgroundPosition;
        clonedElement.style.position = computedStyle.position;
        clonedElement.style.overflow = computedStyle.overflow;
        
        // Preserve all child element styles, especially for gradients and decorative elements
        const allElements = clonedElement.querySelectorAll('*');
        allElements.forEach((el) => {
          const htmlEl = el as HTMLElement;
          // Find matching element in original
          const classList = Array.from(el.classList);
          let originalEl: Element | null = null;
          
          if (classList.length > 0) {
            // Try to find by class
            originalEl = elementToCapture.querySelector(`.${classList.join('.')}`);
          }
          
          if (!originalEl) {
            // Fallback: find by tag and position
            const tagName = el.tagName.toLowerCase();
            const index = Array.from(clonedElement.querySelectorAll(tagName)).indexOf(el);
            const originalElements = elementToCapture.querySelectorAll(tagName);
            if (originalElements[index]) {
              originalEl = originalElements[index];
            }
          }
          
          if (originalEl) {
            const originalStyle = window.getComputedStyle(originalEl as HTMLElement);
            // Copy all important style properties
            htmlEl.style.cssText = originalStyle.cssText;
            // Ensure background gradients are preserved
            if (originalStyle.backgroundImage && originalStyle.backgroundImage !== 'none') {
              htmlEl.style.backgroundImage = originalStyle.backgroundImage;
              htmlEl.style.backgroundSize = originalStyle.backgroundSize;
              htmlEl.style.backgroundPosition = originalStyle.backgroundPosition;
            }
            // Preserve filter effects (blur, etc.)
            if (originalStyle.filter && originalStyle.filter !== 'none') {
              htmlEl.style.filter = originalStyle.filter;
            }
          }
        });
      }
    });

    // Verify canvas has content
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Sample multiple areas of the canvas to check for content
      const sampleSize = 50;
      const samples = [
        { x: 0, y: 0 }, // Top-left
        { x: Math.floor(canvas.width / 2), y: Math.floor(canvas.height / 2) }, // Center
        { x: canvas.width - sampleSize, y: 0 }, // Top-right
        { x: 0, y: canvas.height - sampleSize }, // Bottom-left
        { x: canvas.width - sampleSize, y: canvas.height - sampleSize } // Bottom-right
      ];
      
      let hasContent = false;
      
      for (const sample of samples) {
        try {
          const imageData = ctx.getImageData(sample.x, sample.y, sampleSize, sampleSize);
          const pixels = imageData.data;
          
          // Check if there are any non-transparent pixels
          for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] > 10) { // Alpha channel > 10 (not fully transparent)
              hasContent = true;
              break;
            }
          }
          
          if (hasContent) break;
        } catch (e) {
          // Sample might be out of bounds, continue
        }
      }
      
      if (!hasContent) {
        console.error('❌ Canvas is completely blank - no content detected');
        console.error('Canvas dimensions:', { width: canvas.width, height: canvas.height });
        console.error('Element dimensions:', { 
          width: elementToCapture.offsetWidth, 
          height: elementToCapture.offsetHeight,
          scrollWidth: elementToCapture.scrollWidth,
          scrollHeight: elementToCapture.scrollHeight
        });
        console.error('Element computed styles:', {
          backgroundColor: window.getComputedStyle(elementToCapture).backgroundColor,
          display: window.getComputedStyle(elementToCapture).display,
          visibility: window.getComputedStyle(elementToCapture).visibility,
          opacity: window.getComputedStyle(elementToCapture).opacity
        });
        throw new Error('Canvas capture resulted in blank image - element may not be fully rendered');
      } else {
        console.log('✅ Canvas has content verified');
      }
    }

    // Restore original styles
    if (originalOpacity) elementToCapture.style.opacity = originalOpacity;
    if (originalVisibility) elementToCapture.style.visibility = originalVisibility;
    container.style.opacity = originalContainerOpacity || '0.01';

    // Clean up
    root.unmount();
    if (container.parentNode) {
      document.body.removeChild(container);
    }

    // Convert to base64 image
    const imageDataUrl = canvas.toDataURL('image/png', 1.0);
    
    console.log('✅ Image generated successfully:', {
      dataUrlLength: imageDataUrl.length,
      dataUrlPreview: imageDataUrl.substring(0, 50) + '...'
    });
    
    return imageDataUrl;
  } catch (error) {
    // Clean up on error
    try {
      if (container.parentNode) {
        document.body.removeChild(container);
      }
    } catch (cleanupError) {
      console.error('Error during cleanup:', cleanupError);
    }
    console.error('❌ Error generating gift card image:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}

